import { useEffect, useMemo, useState } from "react"
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Paycode, PaycodeCreate } from "repository/timekeeper/@types/PaycodeModel"
import { normalizeString } from "utils/Validations"
import { usePaycodesActions } from "./usePaycodesActions"

interface Props {
    rows: Paycode[]
    onClose: () => void
    reloading: () => void
    mode: 'add' | 'edit' | 'delete' | 'info'
    selectedRow: Paycode[]
    open: boolean
}

const EMPTY_FORM: PaycodeCreate = {
    name: '',
    unit: 'hours',
    abbreviated_name: '',
    code_number: '',
    type: 'standard',
    timesheet_resolved_exception: false,
    requires_approval: false,
    unjustified_exception: false,
    justified_exception: false,
    asscociate_paycode: false,
    paycode_asociate: null,
    always_process_duration_separate_shift: false,
    multiplier: 1.00,
    add: 0.00,
    description: '',
}

const toFormFromPaycode = (paycode: Paycode): PaycodeCreate => ({
    name: paycode.name ?? '',
    unit: paycode.unit,
    abbreviated_name: paycode.abbreviated_name ?? '',
    code_number: paycode.code_number ?? '',
    type: paycode.type,
    timesheet_resolved_exception: paycode.timesheet_resolved_exception,
    requires_approval: paycode.requires_approval,
    unjustified_exception: paycode.unjustified_exception,
    justified_exception: paycode.justified_exception,
    asscociate_paycode: paycode.asscociate_paycode,
    paycode_asociate: paycode.paycode_asociate,
    always_process_duration_separate_shift: paycode.always_process_duration_separate_shift,
    multiplier: paycode.multiplier ?? 0,
    add: paycode.add ?? 0,
    description: paycode.description ?? '',
})

export const usePaycodeForm = ({ rows, onClose, reloading, mode, selectedRow, open }: Props) => {

    const { addPaycode, updatePaycode } = usePaycodesActions()
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const currentPaycode = mode === 'edit' ? selectedRow[0] : undefined

    const [form, setForm] = useState<PaycodeCreate>(EMPTY_FORM)
    const [initialForm, setInitialForm] = useState<PaycodeCreate>(EMPTY_FORM)
    const [alert, setAlert] = useState(false)

    useEffect(() => {
        if (!open) return

        if (mode === 'edit' && currentPaycode) {
            const preloadedForm = toFormFromPaycode(currentPaycode)
            setForm(preloadedForm)
            setInitialForm(preloadedForm)
            return
        }

        setForm(EMPTY_FORM)
        setInitialForm(EMPTY_FORM)
    }, [open, mode, currentPaycode])

    const update = <K extends keyof PaycodeCreate>(field: K, value: PaycodeCreate[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSubmit = () => {
        const body: PaycodeCreate = {
            ...form,
            abbreviated_name: form.abbreviated_name === '' ? null : form.abbreviated_name,
            code_number: form.code_number === '' ? null : form.code_number,
            description: form.description === '' ? null : form.description,
        }

        if (mode === 'edit' && currentPaycode) {
            updatePaycode({
                setLoading: () => {},
                paycodeId: currentPaycode.id,
                body,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.paycodes.table.alerts.error_add_paycode.message'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.paycodes.table.alerts.save_paycode.message'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            })
            return
        }

        addPaycode({
            setLoading: () => {},
            body,
            closing: onClose,
            reloading,
            messageError: {
                title: 'Error',
                message: t('settings.paycodes.table.alerts.error_add_paycode.message'),
                severity: 'error',
            },
            messageSuccess: {
                title: 'Éxito',
                message: t('settings.paycodes.table.alerts.add_paycode.message'),
                severity: 'success',
            },
            handlerAlert: (message) => {
                enqueueSnackbar(message.message, { variant: message.severity });
            },
        })
    }

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => {
                if (mode === 'edit' && currentPaycode && row.id === currentPaycode.id) return false
                return normalizeString(row.name) === normalizedInput
            });
        };
    }, [rows, mode, currentPaycode]);

    const existCodeNumber = useMemo(() => {
        return (codeNumber: string | null) => {
            if (!codeNumber) return false
            const codeNumberNormalized = String(codeNumber).toLowerCase().trim()
            return rows.some((row) => {
                if (mode === 'edit' && currentPaycode && row.id === currentPaycode.id) return false
                return String(row.code_number ?? '').toLowerCase().trim() === codeNumberNormalized
            })
        }
    }, [rows, mode, currentPaycode])

    const errorInCodeNumber = useMemo(() => {
        return (codeNumber: string) => {
            if (codeNumber === "") return true;
            const regex = /^[a-zA-Z0-9]+$/;
            return regex.test(codeNumber);
        };
    }, []);
    const disabled = useMemo(() => {
        return (
        (mode === 'edit' && !currentPaycode) ||
        form.name === '' ||
        existName(form.name) ||
        existCodeNumber(form.code_number) ||
        (form.asscociate_paycode && !form.paycode_asociate)
        )
    }, [form, mode, currentPaycode, existName, existCodeNumber])

    const cleanForm = () => {
        setForm(EMPTY_FORM)
        setInitialForm(EMPTY_FORM)
        setAlert(false);
        onClose();
    }

    const hasChanges = useMemo(() => {
        return (
            form.name !== initialForm.name ||
            form.abbreviated_name !== initialForm.abbreviated_name ||
            form.code_number !== initialForm.code_number ||
            form.type !== initialForm.type ||
            form.unit !== initialForm.unit ||
            form.timesheet_resolved_exception !== initialForm.timesheet_resolved_exception ||
            form.requires_approval !== initialForm.requires_approval ||
            form.unjustified_exception !== initialForm.unjustified_exception ||
            form.justified_exception !== initialForm.justified_exception ||
            form.asscociate_paycode !== initialForm.asscociate_paycode ||
            form.paycode_asociate !== initialForm.paycode_asociate ||
            form.always_process_duration_separate_shift !== initialForm.always_process_duration_separate_shift ||
            form.multiplier !== initialForm.multiplier ||
            form.add !== initialForm.add ||
            form.description !== initialForm.description
        )
    }, [form, initialForm])

    const detectChanges = () => {
        if (hasChanges) {
            setAlert(true)
        } else {
            cleanForm();
        }
    }

    return {
        form,
        rows,
        currentPaycodeId: currentPaycode?.id,
        alert,
        disabled,
        update,
        setAlert,
        existName,
        cleanForm,
        handleSubmit,
        detectChanges,
        existCodeNumber,
        errorInCodeNumber,
    }
}